/**
 * rock5a_audio.js — Rock5a 麦克风音频接收器
 *
 * 接收来自 Rock5a reSpeaker 的原始 PCM 音频流（16kHz, int16, mono），
 * 将 int16 PCM 数据转为 base64，送入讯飞 STT WebSocket 进行语音识别。
 *
 * 替代原有的 getUserMedia → ScriptProcessor → Xunfei 流程。
 */

/**
 * 将 Int16Array PCM 数据转换为 base64 字符串
 * @param {Int16Array} int16Array
 * @returns {string} base64 encoded string
 */
function int16ToBase64(int16Array) {
  const bytes = new Uint8Array(int16Array.buffer, int16Array.byteOffset, int16Array.byteLength)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

/**
 * Rock5a 音频接收器
 *
 * 接收来自 Rock5aConnection 的音频 ArrayBuffer 块，
 * 累积后转换格式并通过回调输出，供 STT 流程使用。
 */
export class Rock5aAudioReceiver {
  constructor() {
    this._buffer = new Int16Array(0)
    this._onFrameReady = null          // fn(base64Pcm) — 每攒够一帧触发
    this._isActive = false
  }

  get isActive() {
    return this._isActive
  }

  /**
   * 注册帧就绪回调
   * @param {Function} fn - fn(base64String) 接收 base64 编码的 PCM 数据
   */
  onFrameReady(fn) {
    this._onFrameReady = fn
  }

  /**
   * 开始接收
   */
  start() {
    this._isActive = true
    this._buffer = new Int16Array(0)
  }

  /**
   * 停止接收
   */
  stop() {
    this._isActive = false
    this._buffer = new Int16Array(0)
  }

  /**
   * 处理来自 WebSocket 的原始二进制音频数据
   * Rock5a 每次发送 4096 samples (8192 bytes) 的 int16 PCM
   *
   * @param {ArrayBuffer} arrayBuffer — Rock5a WebSocket 二进制帧
   */
  feed(arrayBuffer) {
    if (!this._isActive) return

    const incoming = new Int16Array(arrayBuffer)

    // 追加到累积缓冲
    let combined = new Int16Array(this._buffer.length + incoming.length)
    combined.set(this._buffer, 0)
    combined.set(incoming, this._buffer.length)

    // 每 1280 samples (80ms at 16kHz, Xunfei 推荐帧大小) 发送一次
    const FRAME_SAMPLES = 1280
    while (combined.length >= FRAME_SAMPLES) {
      const frame = combined.slice(0, FRAME_SAMPLES)
      const base64 = int16ToBase64(frame)

      if (this._onFrameReady) {
        this._onFrameReady(base64)
      }

      // 保留剩余部分
      const remaining = new Int16Array(combined.length - FRAME_SAMPLES)
      remaining.set(combined.slice(FRAME_SAMPLES))
      combined = remaining
    }

    this._buffer = combined
  }

  /**
   * 清空缓冲，将剩余数据作为最后一帧发送
   */
  flush() {
    if (this._buffer.length > 0 && this._isActive) {
      const base64 = int16ToBase64(this._buffer)
      if (this._onFrameReady) {
        this._onFrameReady(base64)
      }
    }
    this._buffer = new Int16Array(0)
  }
}

export default Rock5aAudioReceiver
